/**
 * 人脉域 hooks（TanStack Query）：联系人/互动/重要日期/承诺/礼物/分组/关系。
 * 注意：listInteractions / listImportantDates / listCommitments / listGifts 均返回 Page<T>。
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  addGroupMembers,
  archiveContact,
  completeCommitment,
  createCommitment,
  createContact,
  createGift,
  createGroup,
  createImportantDate,
  createInteraction,
  createLink,
  createRelation,
  deleteCommitment,
  deleteContact,
  deleteGift,
  deleteGroup,
  deleteImportantDate,
  deleteInteraction,
  deleteLink,
  deleteRelation,
  getContact,
  getContactsDashboard,
  listCommitments,
  listContacts,
  listGifts,
  listGroups,
  listImportantDates,
  listInteractions,
  removeGroupMember,
  updateCommitment,
  updateContact,
  updateGroup,
  updateImportantDate,
  updateInteraction,
} from '../lib/api';
import type {
  CreateCommitmentBody,
  CreateContactBody,
  CreateGiftBody,
  CreateGroupBody,
  CreateImportantDateBody,
  CreateInteractionBody,
  CreateLinkBody,
  CreateRelationBody,
  UpdateCommitmentBody,
  UpdateContactBody,
  UpdateInteractionBody,
} from '../lib/types';

function invalidateContactData(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ['contacts'] });
  qc.invalidateQueries({ queryKey: ['contact'] });
  qc.invalidateQueries({ queryKey: ['contactsDashboard'] });
}

// ==================== 仪表盘 / 联系人 ====================

export function useContactsDashboard() {
  return useQuery({
    queryKey: ['contactsDashboard'],
    queryFn: () => getContactsDashboard(),
  });
}

export function useContacts(query?: {
  groupId?: string;
  favorite?: string;
  needsContact?: string;
  archived?: string;
  sort?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: ['contacts', query ?? {}],
    queryFn: () => listContacts(query),
  });
}

export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: ['contact', id],
    queryFn: () => getContact(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateContactBody) => createContact(body),
    onSuccess: () => invalidateContactData(qc),
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateContactBody }) => updateContact(id, body),
    onSuccess: () => invalidateContactData(qc),
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteContact(id),
    onSuccess: () => invalidateContactData(qc),
  });
}

export function useArchiveContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archiveContact(id),
    onSuccess: () => invalidateContactData(qc),
  });
}

// ==================== 互动 ====================

export function useInteractions(
  contactId: string | undefined,
  query?: { page?: number; pageSize?: number; q?: string },
) {
  return useQuery({
    queryKey: ['interactions', contactId, query ?? {}],
    queryFn: () => listInteractions(contactId as string, query),
    enabled: Boolean(contactId),
  });
}

export function useCreateInteraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, body }: { contactId: string; body: CreateInteractionBody }) =>
      createInteraction(contactId, body),
    onSuccess: () => {
      invalidateContactData(qc);
      qc.invalidateQueries({ queryKey: ['interactions'] });
    },
  });
}

export function useUpdateInteraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateInteractionBody }) =>
      updateInteraction(id, body),
    onSuccess: () => {
      invalidateContactData(qc);
      qc.invalidateQueries({ queryKey: ['interactions'] });
    },
  });
}

export function useDeleteInteraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteInteraction(id),
    onSuccess: () => {
      invalidateContactData(qc);
      qc.invalidateQueries({ queryKey: ['interactions'] });
    },
  });
}

// ==================== 社交链接 / 关系 ====================

export function useCreateLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, body }: { contactId: string; body: CreateLinkBody }) =>
      createLink(contactId, body),
    onSuccess: () => invalidateContactData(qc),
  });
}

export function useDeleteLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lid: string) => deleteLink(lid),
    onSuccess: () => invalidateContactData(qc),
  });
}

export function useCreateRelation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, body }: { contactId: string; body: CreateRelationBody }) =>
      createRelation(contactId, body),
    onSuccess: () => invalidateContactData(qc),
  });
}

export function useDeleteRelation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, rid }: { contactId: string; rid: string }) =>
      deleteRelation(contactId, rid),
    onSuccess: () => invalidateContactData(qc),
  });
}

// ==================== 重要日期 ====================

export function useImportantDates(query?: {
  contactId?: string;
  upcoming?: number;
  page?: number;
  pageSize?: number;
  q?: string;
}) {
  return useQuery({
    queryKey: ['importantDates', query ?? {}],
    queryFn: () => listImportantDates(query),
  });
}

export function useCreateImportantDate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateImportantDateBody) => createImportantDate(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['importantDates'] });
      invalidateContactData(qc);
    },
  });
}

export function useUpdateImportantDate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CreateImportantDateBody> }) =>
      updateImportantDate(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['importantDates'] });
      invalidateContactData(qc);
    },
  });
}

export function useDeleteImportantDate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteImportantDate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['importantDates'] });
      invalidateContactData(qc);
    },
  });
}

// ==================== 承诺 ====================

export function useCommitments(query?: {
  status?: string;
  contactId?: string;
  overdue?: string;
  page?: number;
  pageSize?: number;
  q?: string;
}) {
  return useQuery({
    queryKey: ['commitments', query ?? {}],
    queryFn: () => listCommitments(query),
  });
}

export function useCreateCommitment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCommitmentBody) => createCommitment(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commitments'] });
      invalidateContactData(qc);
    },
  });
}

export function useUpdateCommitment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCommitmentBody }) =>
      updateCommitment(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commitments'] });
      invalidateContactData(qc);
    },
  });
}

export function useCompleteCommitment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => completeCommitment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commitments'] });
      invalidateContactData(qc);
    },
  });
}

export function useDeleteCommitment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCommitment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commitments'] });
      invalidateContactData(qc);
    },
  });
}

// ==================== 礼物 ====================

export function useGifts(query?: {
  contactId?: string;
  direction?: string;
  page?: number;
  pageSize?: number;
  q?: string;
}) {
  return useQuery({
    queryKey: ['gifts', query ?? {}],
    queryFn: () => listGifts(query),
  });
}

export function useCreateGift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGiftBody) => createGift(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gifts'] });
      invalidateContactData(qc);
    },
  });
}

export function useDeleteGift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteGift(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gifts'] });
      invalidateContactData(qc);
    },
  });
}

// ==================== 分组 ====================

export function useGroups() {
  return useQuery({ queryKey: ['groups'], queryFn: () => listGroups() });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGroupBody) => createGroup(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CreateGroupBody> }) =>
      updateGroup(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteGroup(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      invalidateContactData(qc);
    },
  });
}

export function useAddGroupMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, contactIds }: { id: string; contactIds: string[] }) =>
      addGroupMembers(id, contactIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      invalidateContactData(qc);
    },
  });
}

export function useRemoveGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, cid }: { id: string; cid: string }) => removeGroupMember(id, cid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      invalidateContactData(qc);
    },
  });
}
